// WPI directory sync — pulls every enabled Westport member account from
// Entra (Microsoft Graph) and refreshes the internal (isWpi) contact cards:
// title, department, work email, phones. Delegated User.Read.All with the
// signed-in admin's token; the button that triggers this is crm.manage-gated.
import { msalInstance } from './msalConfig'
import { signedInAccount } from './ssoMemory'
import type { Contact } from '@/types/entities'

const SCOPES = ['User.Read.All']

export interface DirUser {
  displayName: string
  givenName: string | null
  surname: string | null
  jobTitle: string | null
  department: string | null
  mail: string | null
  userPrincipalName: string
  businessPhones: string[]
  mobilePhone: string | null
}

async function graphToken(): Promise<string> {
  // The ACTIVE account — the link's person, or whoever just signed in (ssoMemory.ts).
  const account = signedInAccount(msalInstance, msalInstance.getAllAccounts())
  if (!account) throw new Error('No signed-in account')
  try {
    return (await msalInstance.acquireTokenSilent({ account, scopes: SCOPES })).accessToken
  } catch {
    return (await msalInstance.acquireTokenPopup({ account, scopes: SCOPES })).accessToken
  }
}

/** All enabled member users on the westportproperties.net domain. */
export async function fetchWpiDirectory(): Promise<DirUser[]> {
  const token = await graphToken()
  const select = 'displayName,givenName,surname,jobTitle,department,mail,userPrincipalName,businessPhones,mobilePhone'
  let url: string | null =
    `https://graph.microsoft.com/v1.0/users?$filter=${encodeURIComponent("accountEnabled eq true and userType eq 'Member'")}&$select=${select}&$count=true&$top=999`
  const out: DirUser[] = []
  while (url) {
    const res: Response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, ConsistencyLevel: 'eventual' },
    })
    if (!res.ok) throw new Error(`Graph users read failed (${res.status})`)
    const body = (await res.json()) as { value: DirUser[]; '@odata.nextLink'?: string }
    out.push(...body.value)
    url = body['@odata.nextLink'] ?? null
  }
  return out.filter((u) => {
    const upn = (u.mail ?? u.userPrincipalName ?? '').toLowerCase()
    // Real people only: on our domain, with a parsed human name (skips
    // room/shared/service mailboxes, which carry only a display name).
    return upn.endsWith('@westportproperties.net') && !!(u.givenName && u.surname)
  })
}

export interface SyncPlanItem {
  kind: 'create' | 'update'
  contactId: string
  patch: Partial<Contact>
  create?: Contact
}

/** Diff the directory against current contacts. Pure — caller persists. */
export function planDirectorySync(users: DirUser[], contacts: Contact[]): SyncPlanItem[] {
  const byEmail = new Map<string, Contact>()
  for (const c of contacts) {
    for (const e of [c.workEmail, c.personalEmail]) {
      if (e) byEmail.set(e.toLowerCase(), c)
    }
  }
  const items: SyncPlanItem[] = []
  for (const u of users) {
    const email = (u.mail ?? u.userPrincipalName).toLowerCase()
    const existing = byEmail.get(email)
    const fresh: Partial<Contact> = {
      title: u.jobTitle ?? undefined,
      department: u.department ?? undefined,
      workEmail: u.mail ?? u.userPrincipalName,
      officePhone: u.businessPhones?.[0] ?? undefined,
      cellPhone: u.mobilePhone ?? undefined,
      isWpi: true,
    }
    if (existing) {
      const patch: Partial<Contact> = {}
      for (const k of Object.keys(fresh) as (keyof Contact)[]) {
        const v = fresh[k]
        if (v != null && v !== existing[k]) (patch as Record<string, unknown>)[k] = v
      }
      if (Object.keys(patch).length) items.push({ kind: 'update', contactId: existing.id, patch })
    } else {
      const alias = u.userPrincipalName.split('@')[0].toLowerCase().replace(/[^a-z0-9]+/g, '-')
      items.push({
        kind: 'create',
        contactId: `con-wpi-${alias}`,
        patch: {},
        create: {
          id: `con-wpi-${alias}`,
          firstName: u.givenName ?? u.displayName,
          lastName: u.surname ?? '',
          type: 'WPI Employee',
          accountId: 'acct-wpi',
          trackMemberships: [],
          ...fresh,
        } as Contact,
      })
    }
  }
  return items
}
